import React, { forwardRef } from "react";
import cn from "classnames";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { useComboxContext } from "~/components/shared/Combox/ComboxContext";
import Icon from "~/components/shared/Icon";

export interface ComboxTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  placeholder?: string;
  leadingIcon?: string;
  clearable?: boolean;
  itemLabels?: Record<string, React.ReactNode>;
  subtle?: boolean;
  disclosure?: boolean;
  onMouseEnter?: () => void;
  onFocus?: () => void;
  "data-testid"?: string;
}

const ComboxSelectedItemsInTrigger = ({
  itemLabels = {},
}: {
  itemLabels?: Record<string, React.ReactNode>;
}) => {
  const { selectedValues, onValuesChange, disabled } = useComboxContext();

  const handleRemoveItem = React.useCallback(
    (valueToRemove: string) => {
      if (disabled || !onValuesChange) {
        return;
      }

      const newValues = selectedValues.filter((value) => value !== valueToRemove);
      onValuesChange(newValues);
    },
    [disabled, onValuesChange, selectedValues],
  );

  return (
    <>
      {selectedValues.map((value) => (
        <span
          key={value}
          className="badge badge--outline tabular-numerals flex items-center gap-0.5 py-0 pl-1 pr-0 text-xs hover:bg-gray-100"
        >
          <span className="truncate">{itemLabels[value] || value}</span>
          {!disabled && (
            <span
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleRemoveItem(value);
              }}
              className="flex cursor-pointer items-center justify-center hover:opacity-60"
              role="button"
              tabIndex={-1}
            >
              <Icon icon="close" className="h-4 w-4" />
            </span>
          )}
        </span>
      ))}
    </>
  );
};

export const ComboxTrigger = forwardRef<HTMLButtonElement, ComboxTriggerProps>(
  (
    {
      children,
      className,
      style,
      placeholder,
      disabled,
      leadingIcon,
      clearable,
      itemLabels,
      subtle,
      disclosure = true,
      onMouseEnter,
      onFocus,
      "data-testid": dataTestId,
      ...buttonProps
    },
    ref,
  ) => {
    const {
      open,
      selectedValue,
      selectedValues,
      disabled: contextDisabled,
      triggerRef,
      onValueChange,
      onValuesChange,
      multiple,
    } = useComboxContext();

    const isDisabled = disabled ?? contextDisabled;

    const handleClear = React.useCallback(
      (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (multiple) {
          if (onValuesChange) {
            onValuesChange([]);
          }
        } else {
          if (onValueChange) {
            onValueChange("");
          }
        }
      },
      [multiple, onValueChange, onValuesChange],
    );

    const buttonRef = ref || triggerRef;

    const displayContent = React.useMemo(() => {
      if (multiple && selectedValues.length > 0) {
        return null; // Will be handled by ComboxSelectedItemsInTrigger
      }

      // If children is provided and is not empty, use it
      if (children && children !== "") {
        return children;
      }

      // If single select and has a selected value with label
      if (!multiple && selectedValue && itemLabels?.[selectedValue]) {
        return itemLabels[selectedValue];
      }

      // Fall back to selectedValue or placeholder
      return selectedValue || placeholder;
    }, [children, multiple, selectedValue, selectedValues, itemLabels, placeholder]);

    return (
      <PopoverPrimitive.Trigger asChild={true}>
        <button
          ref={buttonRef}
          type="button"
          className={cn(
            !subtle && "btn btn-small btn-flex btn-depth h-fit min-h-[30px] pl-2 pr-1",
            "group w-full justify-between gap-1",
            isDisabled && "cursor-not-allowed opacity-50",
            className,
          )}
          {...buttonProps}
          style={style}
          disabled={isDisabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onMouseEnter={onMouseEnter}
          onFocus={onFocus}
          data-testid={dataTestId}
        >
          <div className="flex min-h-0 w-full flex-col">
            {multiple && selectedValues.length > 0 ? (
              <div className={cn(!subtle && "py-1", "flex h-full flex-wrap items-center gap-1")}>
                <ComboxSelectedItemsInTrigger itemLabels={itemLabels} />
              </div>
            ) : (
              <span
                className={cn(
                  "flex items-center gap-1.5 truncate text-black",
                  placeholder && !selectedValue && "text-charcoal-300",
                )}
              >
                {leadingIcon && (
                  <Icon icon={leadingIcon} className="text-charcoal-300 h-4 w-4 self-start" />
                )}
                {typeof displayContent === "string" ? (
                  <span dangerouslySetInnerHTML={{ __html: displayContent }} />
                ) : (
                  displayContent
                )}
              </span>
            )}
          </div>
          {(disclosure || clearable) && (
            <div className="ml-auto flex items-center gap-1 self-start pt-0.5">
              {clearable && (multiple ? selectedValues.length > 0 : selectedValue) && (
                <span
                  onClick={handleClear}
                  className="flex cursor-pointer items-center justify-center text-black hover:opacity-60"
                  role="button"
                  aria-label="Clear selection"
                  data-title-tooltip="Clear selection"
                  tabIndex={-1}
                >
                  <Icon icon="heroicons/outline/x-circle" className="h-5 w-5" />
                </span>
              )}
              {disclosure && (
                <Icon
                  icon={subtle ? "down-triangle" : "custom/outline/chevron-down"}
                  className={cn(
                    subtle
                      ? "mt-1 h-2 w-2 opacity-0 transition-all duration-150 group-hover:opacity-100 group-focus:opacity-100 group-active:opacity-100"
                      : "h-6 w-6",
                    open && "rotate-180 opacity-100",
                  )}
                />
              )}
            </div>
          )}
        </button>
      </PopoverPrimitive.Trigger>
    );
  },
);

export default ComboxTrigger;
